import { useNavigate } from 'react-router-dom';
import { useAppState, ACTION_TYPES } from '../hooks/useAppState';
import { getTheme } from '../lib/theme';
import { determinarObligaciones, calcularMuestraGIII } from '../lib/scoring';
import { Users } from 'lucide-react';

export default function CuestionariosPage() {
  const { state, dispatch } = useAppState();
  const navigate = useNavigate();
  const t = getTheme(state.darkMode);
  const dark = state.darkMode;
  const centro = state.centrosTrabajo[state.centroActualIdx];
  if (!centro) return null;

  const trabajadores = centro.trabajadores || [];
  const cuestionarios = centro.cuestionariosCompletos || {};
  const fichas = centro.fichas || {};
  const consentimientos = centro.consentimientos || {};
  const obligaciones = determinarObligaciones(centro.numTrabajadores);
  const muestraGIII = calcularMuestraGIII(centro.numTrabajadores);
  const totalG3Done = trabajadores.filter(tw => cuestionarios[`${tw.id}_guiaIII`]).length;
  const muestraRaw = state.muestraSeleccionada[centro?.id];
  const muestraActual = muestraRaw ? (Array.isArray(muestraRaw) ? new Set(muestraRaw) : muestraRaw) : new Set();

  const iniciarCuestionario = (trabajador, guia) => {
    dispatch({ type: ACTION_TYPES.SET_CUESTIONARIO_ACTIVO, payload: { trabajador, guia, respuestas: {} } });
    navigate('/cuestionarios/aplicar');
  };

  const thStyle = { padding: '10px 8px', textAlign: 'center', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' };
  const cellStyle = { padding: '8px', textAlign: 'center', borderTop: `1px solid ${dark ? 'rgba(255,255,255,0.04)' : '#f3f4f6'}` };
  const btnBase = { padding: '5px 12px', borderRadius: 8, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', border: 'none' };
  const btnApply = { ...btnBase, background: t.accentLight, color: t.textLink, border: `1px solid ${t.accentBorder}` };
  const doneIcon = <span style={{ color: '#10b981', fontSize: '0.78rem', fontWeight: 600 }}>✓</span>;
  const dash = <span style={{ color: t.textFaint, fontSize: '0.7rem' }}>—</span>;

  return (
    <div>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: t.textStrong, marginBottom: 4 }}>Aplicar cuestionarios</h1>
      <p style={{ color: t.textMuted, fontSize: '0.88rem', marginBottom: 20 }}>{centro.nombreCentro} · Selecciona un trabajador para aplicar cuestionarios.</p>

      {/* Muestra GIII banner */}
      {obligaciones.nivel >= 3 && muestraGIII && (
        <div style={{ padding: '10px 16px', background: 'rgba(99,102,241,0.08)', borderRadius: 10, marginBottom: 16, fontSize: '0.78rem', color: dark ? '#a5b4fc' : '#6366f1', border: '1px solid rgba(99,102,241,0.15)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>📊 <strong>Guía III — Muestra (NOM-035 III.1):</strong> Mínimo <strong>{muestraGIII}</strong> de {centro.numTrabajadores} trabajadores.</span>
            <span style={{ color: totalG3Done >= muestraGIII ? '#10b981' : '#f59e0b', fontWeight: 600 }}>{totalG3Done}/{muestraGIII} aplicados</span>
          </div>
          {muestraActual.size > 0 ? (
            <div style={{ marginTop: 6, color: '#10b981' }}>✓ Muestra asignada: {muestraActual.size} trabajadores</div>
          ) : (
            <div style={{ marginTop: 6, color: t.textMuted }}>Configura la estrategia de muestreo en la página de <a href="/trabajadores" style={{ color: dark ? '#a5b4fc' : '#6366f1', fontWeight: 600 }}>Trabajadores</a>.</div>
          )}
        </div>
      )}

      {trabajadores.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 60, color: t.textFaint, textAlign: 'center' }}>
          <Users size={32} />
          <p>Primero registra trabajadores.</p>
          <button onClick={() => navigate('/trabajadores')} style={{ background: 'none', border: 'none', color: t.textLink, fontSize: '0.85rem', cursor: 'pointer', fontWeight: 500 }}>Ir a trabajadores →</button>
        </div>
      ) : (
        <div style={{ borderRadius: 12, overflow: 'hidden', border: `1px solid ${t.border}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: dark ? 'rgba(255,255,255,0.03)' : '#f9fafb' }}>
                <th style={{ ...thStyle, textAlign: 'left', paddingLeft: 14 }}>Trabajador</th>
                <th style={{ ...thStyle, color: '#a855f7', width: 90 }}>Consent.</th>
                <th style={{ ...thStyle, color: '#f59e0b', width: 80 }}>Ficha</th>
                <th style={{ ...thStyle, color: t.textLink, width: 80 }}>Guía I</th>
                {obligaciones.guias.includes("II") && <th style={{ ...thStyle, color: t.textLink, width: 80 }}>Guía II</th>}
                {obligaciones.nivel >= 3 && <th style={{ ...thStyle, color: t.textLink, width: 80 }}>Guía III</th>}
              </tr>
            </thead>
            <tbody>
              {trabajadores.map(tw => {
                const consentOk = !!consentimientos[tw.id];
                const fichaOk = !!fichas[tw.id];
                const g1Done = !!cuestionarios[`${tw.id}_guiaI`];
                const g2Done = !!cuestionarios[`${tw.id}_guiaII`];
                const g3Done = !!cuestionarios[`${tw.id}_guiaIII`];
                const enMuestra = muestraActual.size === 0 || muestraActual.has(tw.id);

                return (
                  <tr key={tw.id}>
                    <td style={{ padding: '10px 14px', borderTop: `1px solid ${dark ? 'rgba(255,255,255,0.04)' : '#f3f4f6'}` }}>
                      <div style={{ fontWeight: 600, color: t.textStrong, fontSize: '0.85rem' }}>{tw.nombreCompleto || `${tw.nombre} ${tw.apellidos || ''}`}</div>
                      <div style={{ fontSize: '0.72rem', color: t.textMuted }}>
                        #{tw.numEmpleado}{fichaOk && <span> · {fichas[tw.id].area} · {fichas[tw.id].puesto}</span>}
                      </div>
                    </td>
                    <td style={cellStyle}>{consentOk ? doneIcon : <button style={{ ...btnBase, background: 'rgba(168,85,247,0.12)', color: '#a855f7' }} onClick={() => { dispatch({ type: ACTION_TYPES.SET_CUESTIONARIO_ACTIVO, payload: { trabajador: tw, guia: null, respuestas: {} } }); navigate('/consentimiento'); }}>Firmar</button>}</td>
                    <td style={cellStyle}>{fichaOk ? doneIcon : consentOk ? <button style={{ ...btnBase, background: 'rgba(245,158,11,0.12)', color: '#f59e0b' }} onClick={() => { dispatch({ type: ACTION_TYPES.SET_CUESTIONARIO_ACTIVO, payload: { trabajador: tw, guia: null, respuestas: {} } }); navigate('/ficha/' + tw.id); }}>Llenar</button> : dash}</td>
                    <td style={cellStyle}>{g1Done ? doneIcon : fichaOk ? <button style={btnApply} onClick={() => iniciarCuestionario(tw, 'I')}>Aplicar</button> : dash}</td>
                    {obligaciones.guias.includes("II") && <td style={cellStyle}>{g2Done ? doneIcon : fichaOk ? <button style={btnApply} onClick={() => iniciarCuestionario(tw, 'II')}>Aplicar</button> : dash}</td>}
                    {obligaciones.nivel >= 3 && <td style={cellStyle}>{g3Done ? doneIcon : !fichaOk ? dash : enMuestra ? <button style={btnApply} onClick={() => iniciarCuestionario(tw, 'III')}>Aplicar</button> : <span style={{ color: t.textFaint, fontSize: '0.65rem', fontStyle: 'italic' }}>No en muestra</span>}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
